import { device } from './device.js';
import { i18n } from './i18n.js';

/* Dictado por voz para las cajas de búsqueda. En la app de Android usa el reconocedor del sistema a través del plugin
   del reproductor (`voice`, el diálogo de Google); en el navegador, la API de voz de la web si existe (Chrome, Edge,
   Safari). Si no hay ninguna, el botón del micrófono no se muestra. En la app de Windows (Electron) tampoco: su Chromium no
   lleva la clave de Google para la voz y el dictado fallaría siempre (se puede dictar con Win+H). */

const LANG = { es: 'es-ES', en: 'en-US', fr: 'fr-FR', de: 'de-DE', pt: 'pt-BR', it: 'it-IT' };

function nativeBridge() {
    try {
        const b = window.cordova && window.cordova.plugins && window.cordova.plugins.filotecaPlayer;
        return b && b.voice ? b : null;
    } catch (e) {
        return null;
    }
}

const webRecognition = () => (typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;

export const voice = {
    /** ¿Se puede dictar aquí? En la tele no (el mando ya tiene su propio micrófono con el asistente) ni en la app de Windows. */
    available() {
        if (device.isTV || device.isElectron) return false;
        return !!(nativeBridge() || webRecognition());
    },

    /**
     * Comprobación completa. En Android pregunta al plugin si el móvil tiene de verdad un reconocedor de voz (hay móviles sin
     * los servicios de Google); si no se puede saber (plugin antiguo, error), se deja intentar. En el navegador vale `available()`.
     */
    async check() {
        if (!this.available()) return false;
        const native = nativeBridge();
        if (native && typeof native.voiceAvailable === 'function') {
            try { return Boolean(Number(await native.voiceAvailable())); } catch (e) { return true; }
        }
        return true;
    },

    /** Escucha y devuelve el texto. Rechaza con Error('cancelled' | 'unavailable' | 'denied' | …). */
    listen() {
        const lang = LANG[i18n.lang] || 'es-ES';
        const native = nativeBridge();
        if (native) {
            return native.voice(lang).then((text) => String(text || '').trim(), (e) => { throw new Error(String(e && e.message ? e.message : e || 'cancelled')); });
        }
        const Rec = webRecognition();
        if (!Rec) return Promise.reject(new Error('unavailable'));
        return new Promise((resolve, reject) => {
            const rec = new Rec();
            rec.lang = lang;
            rec.interimResults = false;
            rec.maxAlternatives = 1;
            let done = false;
            rec.onresult = (e) => { done = true; resolve(String((e.results[0] && e.results[0][0] && e.results[0][0].transcript) || '').trim()); };
            // «network»: el navegador no trae servicio de voz (Brave, Chromium sin claves…); antes se tomaba por «cancelado» y no se avisaba.
            rec.onerror = (e) => {
                if (done) return;
                done = true;
                const code = e && e.error;
                reject(new Error(code === 'not-allowed' || code === 'service-not-allowed' ? 'denied' : (code === 'network' || code === 'audio-capture' || code === 'language-not-supported') ? 'unavailable' : 'cancelled'));
            };
            rec.onend = () => { if (!done) { done = true; reject(new Error('cancelled')); } };
            try { rec.start(); } catch (err) { reject(new Error('unavailable')); }
        });
    }
};

/**
 * Cablea el botón del micrófono de una caja de búsqueda (`[data-voice]` junto a `[data-search]`): lo muestra si hay
 * dictado, pone el resultado en la caja y avisa con un evento `input` para que la búsqueda se ejecute sola.
 */
export function bindVoice(root, { onError = () => {} } = {}) {
    const btn = root.querySelector('[data-voice]');
    const input = root.querySelector('[data-search]');
    if (!btn || !input) return;
    if (!voice.available()) { btn.hidden = true; return; }
    btn.hidden = false;
    // En Android se confirma con el plugin que el móvil tiene reconocedor; si no, el botón se esconde (en un instante).
    voice.check().then((ok) => { if (!ok) btn.hidden = true; }).catch(() => {});
    let busy = false;
    btn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (busy) return;
        busy = true;
        btn.classList.add('is-listening');
        try {
            const text = await voice.listen();
            if (text) {
                input.value = text.replace(/[.。]+$/, '');
                input.dispatchEvent(new Event('input', { bubbles: true }));
            }
        } catch (err) {
            if (err.message !== 'cancelled') onError(err.message);
        } finally {
            busy = false;
            btn.classList.remove('is-listening');
        }
    });
}
